/**
 * Pure decision logic for `rehydrateActiveCallFromBackend` (2026-08 zombie
 * reconnect / false "2 devices" fix). Extracted so the state machine has
 * regression tests: a user-ended tombstone must ALWAYS win over a stale
 * backend SESSION row, and the "active elsewhere" prompt may only arise from
 * a claim the backend already vetted as fresh AND that names a different
 * device. Anything ambiguous degrades to doing nothing — rehydrate is a
 * recovery path and must never destabilize a live session.
 */

export type RehydrateActiveCall = {
  callId: string
  callerId: string
  calleeId: string
  accepted: boolean
  /** This USER's own fresh device claim (backend returns null when stale). */
  activeDeviceId: string | null
}

export type RehydrateDecision =
  /** Nothing to do (no live backend call, or we're not a participant). */
  | 'none'
  /** Backend has no live call — clear a lingering local 'elsewhere' banner. */
  | 'clear-elsewhere'
  /**
   * The user already ended this call on this device: do NOT resurrect it;
   * fire a best-effort END so the stale backend row is settled and deleted.
   */
  | 'self-heal-end'
  /** Same user's OTHER device provably owns media — show the advisory banner. */
  | 'elsewhere'
  /** This device should rejoin the live call (return-to-call). */
  | 'rejoin'

export function decideRehydrateAction(input: {
  active: RehydrateActiveCall | null
  currentUserId: string
  thisDeviceId: string | null | undefined
  existingPhase: string | null
  isTombstoned: (callId: string) => boolean
}): RehydrateDecision {
  const { active, currentUserId, thisDeviceId, existingPhase } = input
  if (!active || !active.accepted) {
    return existingPhase === 'elsewhere' ? 'clear-elsewhere' : 'none'
  }
  if (active.callerId !== currentUserId && active.calleeId !== currentUserId) return 'none'
  if (input.isTombstoned(active.callId)) return 'self-heal-end'
  const claimedDevice = typeof active.activeDeviceId === 'string' ? active.activeDeviceId.trim() : ''
  if (claimedDevice && thisDeviceId && claimedDevice !== thisDeviceId) return 'elsewhere'
  // A vanished sibling claim must NEVER auto-join this device into the call
  // (closed-tab / expired web session → no reconnect). Return-to-call is only
  // for a cold start where this device has no elsewhere banner.
  if (existingPhase === 'elsewhere') return 'clear-elsewhere'
  return 'rejoin'
}
